import React, { useState, useEffect } from 'react';
import { X, Repeat, CreditCard } from 'lucide-react';

const QuickEntryForm = ({ categories, onSubmit, onClose, editingExpense }) => {
  const [formData, setFormData] = useState({
    categoryId: '',
    description: '',
    amount: '',
    expenseDate: new Date()?.toISOString()?.split('T')?.[0],
    notes: '',
    vendor: '',
    client: '',
    bank: '',
    // New recurring/installment fields
    isRecurring: false,
    recurrenceFrequency: 'monthly',
    recurrenceEndDate: '',
    installmentCount: '',
    currentInstallment: 1
  });

  const [expenseType, setExpenseType] = useState('single');

  // Reset form when modal closes or editingExpense changes
  useEffect(() => {
    if (editingExpense) {
      setFormData({
        categoryId: editingExpense?.category || '',
        description: editingExpense?.description || '',
        amount: editingExpense?.amount?.toString() || '',
        expenseDate: editingExpense?.date || new Date()?.toISOString()?.split('T')?.[0],
        notes: editingExpense?.notes || '',
        vendor: editingExpense?.vendor || '',
        client: editingExpense?.client || '',
        bank: editingExpense?.bank || '',
        isRecurring: false,
        recurrenceFrequency: 'monthly',
        recurrenceEndDate: '',
        installmentCount: '',
        currentInstallment: 1
      });
      setExpenseType('single');
    } else {
      // Reset to default values for new expense
      setFormData({
        categoryId: '',
        description: '',
        amount: '',
        expenseDate: new Date()?.toISOString()?.split('T')?.[0],
        notes: '',
        vendor: '',
        client: '',
        bank: '',
        isRecurring: false,
        recurrenceFrequency: 'monthly',
        recurrenceEndDate: '',
        installmentCount: '',
        currentInstallment: 1
      });
      setExpenseType('single');
    }
  }, [editingExpense]);

  const handleSubmit = (e) => {
    e?.preventDefault();
    
    const submitData = {
      ...formData,
      category: formData?.categoryId,
      date: formData?.expenseDate,
      isRecurring: expenseType !== 'single',
      recurrenceFrequency: expenseType === 'recurring' ? formData?.recurrenceFrequency : 'once',
      installmentCount: expenseType === 'installment' ? parseInt(formData?.installmentCount) : null,
      currentInstallment: expenseType === 'installment' ? 1 : null
    };
    
    onSubmit(submitData);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e?.target;
    setFormData(prev => ({ 
      ...prev, 
      [name]: type === 'checkbox' ? checked : value 
    }));
  };

  const handleCancel = () => {
    // Reset form state
    setFormData({
      categoryId: '',
      description: '',
      amount: '',
      expenseDate: new Date()?.toISOString()?.split('T')?.[0],
      notes: '',
      vendor: '',
      client: '',
      bank: '',
      isRecurring: false,
      recurrenceFrequency: 'monthly',
      recurrenceEndDate: '',
      installmentCount: '',
      currentInstallment: 1
    });
    setExpenseType('single');
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={handleCancel}>
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e?.stopPropagation()}>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-gray-900">
            {editingExpense ? 'Editar Despesa' : 'Nova Despesa'}
          </h2>
          <button 
            type="button"
            onClick={handleCancel} 
            className="text-gray-500 hover:text-gray-700 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Expense Type Selection - Only show for new expenses */}
          {!editingExpense && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tipo de Despesa
              </label>
              <div className="grid grid-cols-3 gap-3">
                <button
                  type="button"
                  onClick={() => setExpenseType('single')}
                  className={`p-3 border-2 rounded-lg transition-all ${
                    expenseType === 'single' ? 'border-blue-600 bg-blue-50' : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <div className="text-center">
                    <div className="font-medium text-gray-900">Única</div>
                    <div className="text-xs text-gray-500 mt-1">Despesa pontual</div>
                  </div>
                </button>
                
                <button
                  type="button"
                  onClick={() => setExpenseType('recurring')}
                  className={`p-3 border-2 rounded-lg transition-all ${
                    expenseType === 'recurring' ? 'border-blue-600 bg-blue-50' : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <div className="text-center">
                    <Repeat className="w-5 h-5 mx-auto mb-1 text-gray-700" />
                    <div className="font-medium text-gray-900">Recorrente</div>
                    <div className="text-xs text-gray-500 mt-1">Mensal, anual...</div>
                  </div>
                </button>
                
                <button
                  type="button"
                  onClick={() => setExpenseType('installment')}
                  className={`p-3 border-2 rounded-lg transition-all ${
                    expenseType === 'installment' ? 'border-blue-600 bg-blue-50' : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <div className="text-center">
                    <CreditCard className="w-5 h-5 mx-auto mb-1 text-gray-700" />
                    <div className="font-medium text-gray-900">Parcelada</div>
                    <div className="text-xs text-gray-500 mt-1">Cartão de crédito</div>
                  </div>
                </button>
              </div>
            </div>
          )}

          {/* Basic Fields */}
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Categoria *
              </label>
              <select
                name="categoryId"
                value={formData?.categoryId}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Selecione uma categoria</option>
                {categories?.map(cat => (
                  <option key={cat?.id} value={cat?.id}>{cat?.name}</option>
                ))}
              </select>
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Fornecedor/Vendedor *
              </label>
              <input
                type="text"
                name="vendor"
                value={formData?.vendor}
                onChange={handleChange}
                required
                placeholder="Nome do fornecedor ou vendedor"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Descrição *
              </label>
              <input
                type="text"
                name="description"
                value={formData?.description}
                onChange={handleChange}
                required
                placeholder="Ex: Conta de luz, Aluguel, Compra no cartão..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Valor *
              </label>
              <input
                type="number"
                name="amount"
                value={formData?.amount}
                onChange={handleChange}
                step="0.01"
                min="0"
                required
                placeholder="0,00"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Data {expenseType === 'installment' ? 'da Primeira Parcela' : ''} *
              </label>
              <input
                type="date"
                name="expenseDate"
                value={formData?.expenseDate}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Cliente (Opcional)
              </label>
              <input
                type="text"
                name="client"
                value={formData?.client}
                onChange={handleChange}
                placeholder="Associar a um cliente"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Banco (Opcional)
              </label>
              <select
                name="bank"
                value={formData?.bank}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Selecione um banco</option>
                <option value="Banco do Brasil">Banco do Brasil</option>
                <option value="Itaú">Itaú</option>
                <option value="Bradesco">Bradesco</option>
                <option value="Santander">Santander</option>
                <option value="Caixa">Caixa Econômica</option>
                <option value="Nubank">Nubank</option>
                <option value="Inter">Inter</option>
                <option value="Outro">Outro</option>
              </select>
            </div>
          </div>

          {/* Recurring Expense Fields */}
          {expenseType === 'recurring' && !editingExpense && (
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg space-y-3">
              <h3 className="font-medium text-gray-900 flex items-center gap-2">
                <Repeat className="w-4 h-4" />
                Configurações de Recorrência
              </h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Frequência *
                  </label>
                  <select
                    name="recurrenceFrequency"
                    value={formData?.recurrenceFrequency}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="weekly">Semanal</option>
                    <option value="biweekly">Quinzenal</option>
                    <option value="monthly">Mensal</option>
                    <option value="quarterly">Trimestral</option>
                    <option value="yearly">Anual</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Repetir até (opcional)
                  </label>
                  <input
                    type="date"
                    name="recurrenceEndDate"
                    value={formData?.recurrenceEndDate}
                    onChange={handleChange}
                    min={formData?.expenseDate}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              <p className="text-xs text-gray-600">
                💡 Esta despesa será gerada automaticamente nos próximos meses de acordo com a frequência selecionada
              </p>
            </div>
          )}

          {/* Installment Expense Fields */}
          {expenseType === 'installment' && !editingExpense && (
            <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg space-y-3">
              <h3 className="font-medium text-gray-900 flex items-center gap-2">
                <CreditCard className="w-4 h-4" />
                Configurações de Parcelamento
              </h3>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Número de Parcelas *
                </label>
                <input
                  type="number"
                  name="installmentCount"
                  value={formData?.installmentCount}
                  onChange={handleChange}
                  min="2"
                  max="48"
                  required
                  placeholder="Ex: 12"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {formData?.installmentCount && formData?.amount && (
                <div className="bg-white p-3 rounded border border-purple-300">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Valor por parcela:</span>
                    <span className="font-medium text-gray-900">
                      R$ {(parseFloat(formData?.amount) / parseInt(formData?.installmentCount))?.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm mt-1">
                    <span className="text-gray-600">Total:</span>
                    <span className="font-medium text-gray-900">
                      R$ {parseFloat(formData?.amount)?.toFixed(2)}
                    </span>
                  </div>
                </div>
              )}

              <p className="text-xs text-gray-600">
                💡 Serão criadas automaticamente todas as parcelas com vencimentos mensais
              </p>
            </div>
          )}

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Observações
            </label>
            <textarea
              name="notes"
              value={formData?.notes}
              onChange={handleChange}
              rows="3"
              placeholder="Informações adicionais sobre a despesa..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={handleCancel}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              {editingExpense ? 'Salvar Alterações' : 
                expenseType === 'installment' ? `Adicionar ${formData?.installmentCount}x Parcelas` :
                expenseType === 'recurring' ? 'Adicionar Recorrente' : 'Adicionar Despesa'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default QuickEntryForm;